import { Controller } from "@hotwired/stimulus";
import * as ZXing from "@zxing/library";
import * as Turbo from "@hotwired/turbo";
import {
  cookie,
  escapeHTML,
  verifyInterface,
  getTurboResponse,
} from "../utils";
import { playOnce } from "../sound";

const COOKIE_SCAN_ID_KEY = "scan_selected_device_id";

export default class extends Controller {
  /**
   * # Bulk Scan Controller.
   *
   * ## Required API
   * __Targets__
   *  * scanUi - where the scanner will be displayed
   *  * sourceSelect - Video source selection (filled by controller)
   *  * assetDisplayList - ul that holds all scanned assets
   *  * locationInfo - 4 targets that map to success: 0, PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3, no info: 4
   *
   * __Values__
   * * formUrl - url to post 'wattro_id' to. Controller will render Stream or navigate to result.
   *
   * ## Optional (but recommended) API
   * __Targets__
   *  * assetIdList - input
   *  * assetCount - span
   *    _hide/show target_
   *  * showIfNoAssets        - show this element if assetCount === 0
   *  * showIfAssets        - show this element if assetCount > 0
   *  * showIfUniqueAsset     - show this element if assetCount === 1
   *  * showIfMultipleAssets  - show this element if assetCount > 1
   *
   *  __Values__
   * * newAssetScannedMp3 - url to sound file to play on scan
   */
  static targets = [
    "assetCount",
    "assetDisplayList",
    "assetIdList",
    "locationInfo",
    "scanUi",
    "showIfAssets",
    "showIfMultipleAssets",
    "showIfNoAssets",
    "showIfUniqueAsset",
    "sourceSelect",
  ];
  static values = {
    formUrl: String,
    mounted: Boolean,
    positionErrorCode: Number,
    newAssetScannedMp3: String,
  };

  async connect() {
    if (this.isAlreadyConnected()) return;
    console.debug(this.identifier, this);
    verifyInterface(this, {
      requiredInterface: [
        "scanUiTarget",
        "sourceSelectTarget",
        "assetDisplayListTarget",
        "formUrlValue",
      ],
      checkCSRF: true,
    });
    this.toastWrapper = document.getElementById("toast_stream");
    if (!this.toastWrapper) {
      throw new Error(
        "SETUP ERROR  - Failed to find Element with Id toast_stream",
      );
    }
    try {
      await this.setupScanner();
    } catch (e) {
      this.errToUser("Laden des Scanners fehlgeschlagen.", e);
      return;
    }
    try {
      await this.scanFromSelectedCamOrDefault();
    } catch (e) {
      this.errToUser("Starten des Scanners fehlgeschlagen.", e);
      return;
    }
    await this.populateSourceSelect();
    await this.getLocation();
  }

  disconnect() {
    this.resetScanner();
  }

  isAlreadyConnected() {
    if (this.hasMountedValue && this.mountedValue) {
      return true;
    }
    this.mountedValue = true;
    return false;
  }

  resetScanner() {
    this.currentlyHandlingText = undefined;
    this.handledIDs = {};
    this.updateAssetDisplay();
    if (!this.scanner) return;
    this.scanner.reset();
    delete this.scanner;
  }

  async setupScanner() {
    this.currentlyHandlingText = undefined;
    this.coordsStrObj = "";
    this.positionErrorCodeValue = 4; // unknown
    this.handledIDs = {};
    this.updateAssetDisplay();
    if (this.scanner) {
      await this.resetScanner();
    }
    const hints = new Map();
    // see https://github.com/zxing-js/library/blob/master/src/core/MultiFormatReader.ts#L101 on how hints are processed
    hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
      ZXing.BarcodeFormat.QR_CODE,
      ZXing.BarcodeFormat.DATA_MATRIX,
    ]);
    this.scanner = new ZXing.BrowserMultiFormatReader(
      hints /*timeout after successful scan defaults to 500ms */,
    );
    this.selectedDeviceId = cookie.get(COOKIE_SCAN_ID_KEY);
  }

  async scanFromSelectedCamOrDefault() {
    if (!this.scanner) {
      await this.setupScanner();
    } else {
      this.scanner.reset();
    }
    if (this.selectedDeviceId && this.selectedDeviceId !== "") {
      this.scanner.decodeFromVideoDevice(
        this.selectedDeviceId,
        this.scanUiTarget,
        this.handleScan.bind(this),
      );
    } else {
      // try to start from the environment facing cam
      this.scanner.decodeFromConstraints(
        { video: { facingMode: "environment" } },
        this.scanUiTarget,
        this.handleScan.bind(this),
      );
    }
  }

  async populateSourceSelect() {
    if (!this.scanner) {
      await this.setupScanner();
    }
    const videoInputDevices = await this.scanner.listVideoInputDevices();

    if (videoInputDevices.length === 1) {
      const element = videoInputDevices[0];
      this.sourceSelectTarget.disabled = true;
      const sourceOption = document.createElement("option");
      sourceOption.text = `(default) ${element.label}`;
      sourceOption.value = "";
      this.sourceSelectTarget.appendChild(sourceOption);
    } else {
      const sourceOption = document.createElement("option");
      sourceOption.text = "default";
      sourceOption.value = "";

      this.sourceSelectTarget.appendChild(sourceOption);
      for (const element of videoInputDevices) {
        const deviceId = element.deviceId;
        const sourceOption = document.createElement("option");
        sourceOption.text = element.label;
        sourceOption.value = deviceId;
        this.sourceSelectTarget.appendChild(sourceOption);
      }
    }

    this.sourceSelectTarget.onchange = () => {
      this.selectedDeviceId = this.sourceSelectTarget.value;
      cookie.set(COOKIE_SCAN_ID_KEY, this.selectedDeviceId);
      this.scanFromSelectedCamOrDefault();
    };
  }

  handleScan(result, error) {
    if (result) {
      return this.handleSuccessfulScan(result.text, result);
    }
    if (error) {
      return this.handleScanErr(error.message, error);
    }
  }

  handleScanErr(errorMessage /*: string */, error /*: ZXing Exception */) {
    if (error instanceof ZXing.NotFoundException) {
      return;
    }
    try {
      this.errToUser(`Scanner Fehler: ${errorMessage}`, error);
    } catch (e) {
      console.error(e);
      console.warn(error);
    }
  }

  async handleSuccessfulScan(decodedText, scanResult) {
    if (this.currentlyHandlingText === decodedText) return; //early exit if we're already handling this value
    this.currentlyHandlingText = decodedText;
    try {
      return this._handleText(decodedText);
    } catch (exception) {
      this.errToUser(`Fehler beim Verarbeiten von '${decodedText}'.`, {
        scanResult,
        exception,
      });
    } finally {
      this.currentlyHandlingText = undefined;
    }
  }

  _handleText(decodedText) {
    /* should be wrapped by handleSuccessfulScan */
    if (!decodedText) {
      throw new Error("No decodedText");
    }
    const textParts = decodedText.split("/");
    if (decodedText.startsWith("wattro.")) {
      return this.handleWattroID(textParts.at(-1));
    }
    const wattroIndex = textParts.findIndex((part) =>
      part.endsWith(".wattro.io"),
    );
    if (wattroIndex === -1) {
      const url = `/ui/asset/?general_search=${encodeURIComponent(decodedText)}`;
      this.infoToUser(
        `Kein Wattro Label.<br/> <a href='${url}' class="fw-bold">'${decodedText}' suchen</a>.`,
      );
      return;
    }
    const localPath = "/" + textParts.slice(wattroIndex + 1).join("/");
    this.infoToUser(`Wattro Link erkannt.<br/>`, { localPath, decodedText });
    return Turbo.visit(localPath, { action: "advance" });
  }

  async handleWattroID(wattroId) {
    wattroId = wattroId.toLowerCase();
    if (wattroId in this.handledIDs) {
      this.infoToUser(`${wattroId} bereits gescannt.`);
      return;
    }
    this.handledIDs[wattroId] = undefined;
    return this.postToForm(wattroId);
  }

  async postToForm(wattroId) {
    const formData = new FormData();
    formData.append("wattro_id", wattroId);
    formData.append("coords", this.coordsStrObj);
    const { response, is_stream, html } = await getTurboResponse(
      this.formUrlValue,
      formData,
    );
    if (is_stream) {
      let newAsset = true;
      if (response.headers.has("X-ASSET-ID")) {
        const assetId = Number(response.headers.get("X-ASSET-ID"));
        if (this.getHandledAssetIds().includes(assetId)) {
          this.infoToUser("Dieses Gerät ist bereits in der Liste.");
          newAsset = false;
        }
        this.handledIDs[wattroId] = assetId;
      }

      if (newAsset) {
        if (this.hasNewAssetScannedMp3Value) {
          playOnce(this.newAssetScannedMp3Value);
        }
        Turbo.renderStreamMessage(html);
      }
    } else {
      this.resetScanner();
      await history.pushState({ wattro_id: wattroId }, "", response.url);
      document.querySelector("html").innerHTML = html;
    }
    this.updateAssetDisplay();
    this.currentlyHandlingText = undefined;
  }

  assetIdListTargetConnected(element) {
    element.value = JSON.stringify(this.getHandledAssetIds());
  }

  showIfUniqueAssetTargetConnected(element) {
    element.classList.toggle("d-none", this.getHandledAssetIds().length !== 1);
  }

  showIfMultipleAssetsTargetConnected(element) {
    element.classList.toggle("d-none", this.getHandledAssetIds().length < 2);
  }

  getHandledAssetIds() {
    if (this.handledIDs === undefined) return [];

    const assetIds = Object.values(this.handledIDs).filter(
      (id) => id !== undefined,
    );
    return [...new Set(assetIds)];
  }

  updateAssetDisplay() {
    const assetIds = this.getHandledAssetIds();
    this.assetIdListTargets.forEach(
      (inputTarget) => (inputTarget.value = JSON.stringify(assetIds)),
    );

    const assetCount = assetIds.length;
    if (this.hasAssetCountTarget) {
      this.assetCountTarget.innerText = String(assetCount);
    }
    if (assetCount === 0) {
      showElements(this.showIfNoAssetsTargets);
      hideElements([
        ...this.showIfAssetsTargets,
        ...this.showIfUniqueAssetTargets,
        ...this.showIfMultipleAssetsTargets,
      ]);
    } else if (assetCount === 1) {
      showElements([
        ...this.showIfAssetsTargets,
        ...this.showIfUniqueAssetTargets,
      ]);
      hideElements([
        ...this.showIfNoAssetsTargets,
        ...this.showIfMultipleAssetsTargets,
      ]);
    } else {
      showElements([
        ...this.showIfAssetsTargets,
        ...this.showIfMultipleAssetsTargets,
      ]);
      hideElements([
        ...this.showIfNoAssetsTargets,
        ...this.showIfUniqueAssetTargets,
      ]);
    }
  }

  errToUser(text, console_data) {
    console.log(text);
    if (console_data) console.log(console_data);

    const newError = document.createElement("div");
    newError.classList = "toast text-white bg-danger";
    newError.innerHTML = `<div class="toast-header">
            <strong class="me-auto">Scanner Fehler</strong>
            <button type="button"
              class="btn-close"
              data-bs-dismiss="toast"
              aria-label="Close"></button>
              </div>
            <div class="toast-body">${text}</div>`;
    this.toastWrapper.replaceChildren(newError);
  }

  infoToUser(text, console_data) {
    console.log(text);
    if (console_data) console.log(console_data);

    const infoToast = document.createElement("div");
    infoToast.className = "toast";
    infoToast.setAttribute("data-bs-delay", "3000");
    infoToast.innerHTML = `<div class="toast-header"><span class="me-auto">${text}</span>
            <button type="button"
              class="btn-close"
              data-bs-dismiss="toast"
              aria-label="Close"></button>
            </div>`;
    this.toastWrapper.insertAdjacentElement("beforeend", infoToast);
  }

  getLocation() {
    if (!navigator.geolocation) {
      this.positionErrorCodeValue = 2; // GeolocationPositionError.POSITION_UNAVAILABLE;
      return;
    }
    // first cb is with position, second with error
    navigator.geolocation.getCurrentPosition(
      this.handlePosition.bind(this),
      this.handlePositionError.bind(this),
    );
  }

  handlePosition(position) {
    this.coordsStrObj = JSON.stringify({
      // decompose to get a json stringify obj
      accuracy: position.coords.accuracy,
      altitude: position.coords.altitude,
      altitudeAccuracy: position.coords.altitudeAccuracy,
      heading: position.coords.heading,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      speed: position.coords.speed,
    });
    this.positionErrorCodeValue = 0; // ok
  }

  handlePositionError(error) {
    console.error(error);
    this.positionErrorCodeValue = error.code;
  }

  positionErrorCodeValueChanged() {
    this.showCurrentLocationStatus();
  }

  showCurrentLocationStatus() {
    this.locationInfoTargets.forEach((element, index) => {
      element.hidden = index !== this.positionErrorCodeValue;
    });
  }

  pop(event) {
    const assetIdToPop = Number(event.target.value);
    this.handledIDs = Object.fromEntries(
      Object.entries(this.handledIDs).filter(([, id]) => id !== assetIdToPop),
    );
    event.target.closest("li").remove();
    this.updateAssetDisplay();
  }

  popAll() {
    this.handledIDs = {};
    this.assetDisplayListTarget.innerHTML = "";
    this.updateAssetDisplay();
  }

  manualInput(event) {
    const inputField = event.target.querySelector("input[type='text']");
    const userInput = escapeHTML(inputField.value.trim());

    if (!userInput) return;

    if (userInput.length !== 6) {
      this.errToUser(
        `Suche für "${userInput}" abgebrochen.<br>Bitte ein vollständiges Wattro-Label (6 Zeichen) eingeben.`,
      );
      return;
    }

    return this.handleWattroID(userInput);
  }
}

function showElements(elements) {
  elements.forEach((el) => el.classList.remove("d-none"));
}

function hideElements(elements) {
  elements.forEach((el) => el.classList.add("d-none"));
}
